import { useState, useEffect, useRef } from 'react'
import { useScenarios, useScenario } from '../hooks'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { BorderBeam } from '@/components/ui/border-beam'

const HIGHLIGHT_INTERVAL_MS = 60 * 1000 // Run highlight every one minute

interface ScenarioSelectProps {
    objectID: string | null
    value: string | null
    onValueChange: (value: string | null) => void
    className?: string
    labelClassName?: string
    selectClassName?: string
    highlightDuration?: number // Duration in seconds for the animated border
}

export function ScenarioSelect({
    objectID,
    value,
    onValueChange,
    className,
    labelClassName,
    selectClassName,
    highlightDuration = 10, // Default 10 seconds
}: ScenarioSelectProps) {
    const { t } = useTranslation()
    const { data: scenarios = [], isLoading: isLoadingScenarios } = useScenarios(objectID)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [showHighlight, setShowHighlight] = useState(false)
    const scenarioId = value ? parseInt(value) : null
    const { data: scenarioDetails } = useScenario(objectID, scenarioId)

    const selectedScenario = scenarios.find((s: { id: number; name: string; isDefault: boolean }) => s.id.toString() === value)

    const isReady = Boolean(value && !isLoadingScenarios && scenarios.length > 0)
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Run highlight on mount when ready, then every one minute
    useEffect(() => {
        if (!isReady || !objectID) {
            setShowHighlight(false)
            return
        }

        const runCycle = () => {
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
            setShowHighlight(true)
            hideTimerRef.current = setTimeout(() => {
                setShowHighlight(false)
                hideTimerRef.current = null
            }, highlightDuration * 1000)
        }

        // Initial run after a short delay so the component is rendered
        const initialDelay = setTimeout(runCycle, 500)

        // Repeat every one minute
        const intervalId = setInterval(runCycle, HIGHLIGHT_INTERVAL_MS)

        return () => {
            clearTimeout(initialDelay)
            clearInterval(intervalId)
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
        }
    }, [isReady, objectID, highlightDuration])

    return (
        <>
            <div className={className || 'flex items-center gap-2'}>
                <Label className={labelClassName || 'text-xs font-medium text-muted-foreground'}>
                    {t('objects.scenario')}:
                </Label>
                <Select
                    value={value || undefined}
                    onValueChange={(newValue) => onValueChange(newValue || null)}
                    disabled={isLoadingScenarios}
                >
                    <BorderBeam
                        active={showHighlight}
                        duration={2}
                        beamSize={180}
                        strokeWidth={2}
                        borderRadius={6}
                        className="inline-block"
                    >
                        <SelectTrigger className={cn(
                            selectClassName || 'w-[180px] h-8',
                            'text-left [&>span]:font-bold'
                        )}>
                            <SelectValue placeholder={t('objects.selectScenario')} />
                        </SelectTrigger>
                    </BorderBeam>
                    <SelectContent>
                        {scenarios.map((scenario: { id: number; name: string; isDefault: boolean }) => (
                            <SelectItem key={scenario.id} value={scenario.id.toString()}>
                                <span className="text-left"><TranslatableText text={scenario.name} /></span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {value && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => setIsDialogOpen(true)}
                        title={t('objects.viewScenarioDetails')}
                    >
                        <Info className="h-4 w-4" />
                    </Button>
                )}
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{scenarioDetails?.name || selectedScenario?.name || t('objects.scenarioDetails')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.scenarioDetailsDescription')}
                        </DialogDescription>
                    </DialogHeader>
                    {scenarioDetails && (
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm font-semibold">{t('objects.scenarioName')}:</Label>
                                <p className="text-sm text-muted-foreground mt-1"><TranslatableText text={scenarioDetails.name} /></p>
                            </div>
                            <div>
                                <Label className="text-sm font-semibold">{t('objects.description')}:</Label>
                                <p className="text-sm text-muted-foreground mt-1">{scenarioDetails.description ? <TranslatableText text={scenarioDetails.description} /> : '-'}</p>
                            </div>
                            <div>
                                <Label className="text-sm font-semibold">{t('objects.useCase')}:</Label>
                                <p className="text-sm text-muted-foreground mt-1">{scenarioDetails.useCase ? <TranslatableText text={scenarioDetails.useCase} /> : '-'}</p>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

